import { filledRows, reviewDateTime, thousands, zoneOffset } from '../configStudio/configModel';
import { TC } from '../configStudio/kit/ConfigPanel';
import { describeTrigger, Translate } from '../wizard/adapters/ruleAdapter';
import { IReviewRow } from '../wizard/kit/ReviewTable';
import { RuleStep, RuleWizardState } from './ruleModel';

const TR = `${TC}.rule`;
const RV = `${TC}.rule.review`;

const title = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

const transactionLines = (state: RuleWizardState, t: Translate): string[] => {
  const { trigger } = state;
  const parts: string[] = [];
  if (trigger.amountOn && trigger.amount !== '') {
    parts.push(t(`${RV}.amount`, { value: trigger.amount }));
  }
  if (trigger.typeOn && trigger.transactionType !== null) {
    parts.push(t(`${RV}.type`, { value: trigger.transactionType }));
  }
  const lines = parts.length > 0 ? [parts.join('  |  ')] : [];
  if (trigger.propertiesOn) {
    trigger.properties
      .filter(row => row.key.trim() !== '' && row.value.trim() !== '')
      .forEach(row =>
        lines.push(
          t(`${RV}.property`, {
            key: row.key.trim(),
            operator: t(`${RV}.${row.operator === 'equals' ? 'equals' : 'notEquals'}`),
            value: row.value.trim()
          })
        )
      );
  }
  if (state.firstOnly) {
    lines.push(t(`${RV}.firstOnly`));
  }
  return lines;
};

const triggerValue = (state: RuleWizardState, t: Translate) => {
  const kind = state.triggerKind ?? 'transaction';
  const heading = t(`${TR}.trigger.${kind}`);
  if (kind === 'transaction') {
    return [heading, ...transactionLines(state, t)].join('\n');
  }
  if (kind === 'customer') {
    return [heading, t(`${TR}.details.customerEvent.${state.customerEvent}`)].join('\n');
  }
  return [heading, describeTrigger(state.trigger, t).detail].join('\n');
};

const outcomeValue = (state: RuleWizardState, t: Translate) => {
  const { outcome } = state;
  switch (outcome.kind) {
    case 'stamp':
    case 'game':
      return t(`${RV}.${outcome.kind}Outcome`, {
        value: outcome.quantity,
        name: outcome.campaign?.name ?? '',
        id: outcome.campaign?.id ?? ''
      });
    case 'reward':
      return t(`${RV}.rewardOutcome`, {
        name: outcome.reward?.name ?? '',
        id: outcome.reward?.id ?? ''
      });
    case 'points':
      return t(`${RV}.pointsOutcome`, { value: outcome.points, name: outcome.loyalty?.name ?? '' });
    case 'audience':
      return t(`${RV}.audienceOutcome`, { name: outcome.audience?.name ?? '' });
    case 'customer':
      return t(`${RV}.customerOutcome`, {
        value: filledRows(outcome.properties)
          .map(row => `${row.key.trim()} = ${row.value.trim()}`)
          .join(', ')
      });
    default:
      return t(`${RV}.pushOutcome`);
  }
};

const limitsValue = (state: RuleWizardState, t: Translate) => {
  const { limits } = state;
  const first: string[] = [];
  const second: string[] = [];
  if (limits.totalOn) first.push(t(`${RV}.maxTriggers`, { value: thousands(limits.total) }));
  if (state.firstOnly || limits.perPersonOn) {
    first.push(
      t(`${RV}.maxPerPerson`, { value: state.firstOnly ? 1 : thousands(limits.perPerson) })
    );
  }
  if (limits.rateOn) {
    second.push(
      t(`${RV}.ruleRate`, {
        value: thousands(limits.rate),
        period: t(`${TC}.periodTitle.${limits.ratePeriod}`)
      })
    );
  }
  if (limits.userRateOn) {
    second.push(
      t(`${RV}.userRate`, {
        value: thousands(limits.userRate),
        period: t(`${TC}.periodTitle.${limits.userRatePeriod}`)
      })
    );
  }
  const lines = [first, second].filter(line => line.length > 0).map(line => line.join('  |  '));
  return lines.length > 0 ? lines.join('\n') : t(`${TC}.noLimit`);
};

/** The rows of 55, each linking back to the step that answered it. */
export const ruleReviewRows = (
  state: RuleWizardState,
  t: Translate,
  timezone: string,
  now: string
): IReviewRow<RuleStep>[] => {
  const start = state.beginsAt ?? now;
  const dated = (value: string) =>
    t(`${RV}.dateTime`, {
      value: reviewDateTime(value, timezone),
      offset: zoneOffset(timezone, value)
    });
  const { window } = state;
  return [
    { key: 'start', label: t(`${RV}.start`), value: dated(start), edit: 'schedule' },
    {
      key: 'end',
      label: t(`${RV}.end`),
      value: state.endsOn && state.endsAt !== null ? dated(state.endsAt) : t(`${TC}.never`),
      edit: 'schedule'
    },
    {
      key: 'trigger',
      label: t(`${RV}.trigger`),
      value: triggerValue(state, t),
      edit: 'triggerDetails'
    },
    {
      key: 'outcome',
      label: t(`${RV}.outcome`),
      value: outcomeValue(state, t),
      edit: 'outcomeDetails'
    },
    {
      key: 'window',
      label: t(`${RV}.window`),
      value: window.on
        ? `${window.days.map(title).join(', ')}  |  ${window.from} – ${window.to} (UTC${zoneOffset(
            timezone
          )})`
        : t(`${RV}.anyDay`),
      edit: 'schedule'
    },
    { key: 'limits', label: t(`${RV}.limits`), value: limitsValue(state, t), edit: 'schedule' }
  ];
};
